import { Component, ElementRef, forwardRef, HostListener, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ICON_LIBRARIES, IconCatalogService, IconEntry, IconLibrary } from 'src/app/core/services/icon-catalog.service';

const PAGE = 120;

/**
 * Campo de ícone com pré-visualização ao vivo e um painel de pesquisa sobre
 * as bibliotecas carregadas (Remix, MDI, Boxicons, Line Awesome).
 * Funciona com ngModel / formControl; o valor é a classe completa.
 */
@Component({
  selector: 'app-icon-picker',
  templateUrl: './icon-picker.component.html',
  styleUrls: ['./icon-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IconPickerComponent), multi: true },
  ],
})
export class IconPickerComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = 'ri-home-line';
  @Input() fallbackIcon = 'ri-price-tag-3-line';
  @Input() size: 'sm' | 'md' = 'md';

  value = '';
  disabled = false;
  exists = true;
  open = false;

  readonly libraries = ICON_LIBRARIES;
  library: IconLibrary | 'all' = 'all';
  searchTerm = '';
  results: IconEntry[] = [];
  total = 0;
  limit = PAGE;
  counts: Record<string, number> = {};

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private catalog: IconCatalogService, private host: ElementRef<HTMLElement>) {}

  /** Fecha o painel ao clicar fora do componente. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.closePanel();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.closePanel();
  }

  ngOnInit(): void {
    this.refresh();
  }

  // ------------------------------------------------ ControlValueAccessor
  writeValue(value: any): void {
    this.value = `${value ?? ''}`;
    this.exists = !this.value || this.catalog.exists(this.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ------------------------------------------------------------- estado
  get previewClass(): string {
    return this.catalog.normalize(this.value) || this.fallbackIcon;
  }

  onInput(raw: string): void {
    this.value = raw ?? '';
    this.exists = !this.value || this.catalog.exists(this.value);
    this.onChange(this.value.trim() || null);
  }

  onBlur(): void {
    // Completa o prefixo da biblioteca quando só vem o nome (ex.: "mdi-home").
    const normalized = this.catalog.normalize(this.value);
    if (normalized !== this.value) {
      this.value = normalized;
      this.onChange(normalized || null);
    }
    this.onTouched();
  }

  clear(): void {
    this.onInput('');
  }

  pick(entry: IconEntry): void {
    this.value = entry.cls;
    this.exists = true;
    this.onChange(entry.cls);
    this.onTouched();
    this.closePanel();
  }

  openPanel(): void {
    if (this.disabled) return;
    this.open = true;
    this.refresh();
  }

  closePanel(): void {
    this.open = false;
  }

  togglePanel(): void {
    if (this.open) this.closePanel();
    else this.openPanel();
  }

  setLibrary(library: IconLibrary | 'all'): void {
    this.library = library;
    this.limit = PAGE;
    this.refresh();
  }

  onSearch(term: string): void {
    this.searchTerm = term ?? '';
    this.limit = PAGE;
    this.refresh();
  }

  showMore(): void {
    this.limit += PAGE;
    this.refresh();
  }

  trackEntry(_: number, entry: IconEntry): string {
    return entry.name;
  }

  private refresh(): void {
    const all = this.catalog.all();
    if (!Object.keys(this.counts).length) {
      for (const entry of all) this.counts[entry.lib] = (this.counts[entry.lib] ?? 0) + 1;
    }
    const term = this.searchTerm.trim().toLowerCase().replace(/-/g, ' ');
    const filtered = all.filter(
      (entry) =>
        (this.library === 'all' || entry.lib === this.library) &&
        (!term || entry.search.includes(term) || entry.name.includes(term.replace(/ /g, '-'))),
    );
    this.total = filtered.length;
    this.results = filtered.slice(0, this.limit);
  }
}
